import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { AlertTriangle } from "lucide-react";

export function DataResetDialog({ open, onOpenChange, onDone }) {
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => setConfirm("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (confirm !== "RESET") {
      toast.error('Type "RESET" exactly to confirm');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/admin/data/reset", { confirm: "RESET" });
      const d = res.data?.deleted || {};
      const parts = Object.entries(d).map(([k, v]) => `${k}: ${v}`).join(" · ");
      toast.success(`Reset complete — ${parts || "nothing to delete"}`);
      reset();
      onOpenChange(false);
      if (onDone) onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent
        className="sm:max-w-md rounded-xl border-slate-300/70"
        data-testid="data-reset-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-rose-700">
            <AlertTriangle className="h-5 w-5" /> Reset operational data
          </DialogTitle>
          <DialogDescription className="text-xs text-neutral-700">
            This permanently deletes <strong>all promoters, stores, shifts, stock entries,
            and surveys</strong>. The admin account and the SKU master list (survey form
            options) are kept. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-800 space-y-1">
            <div><strong>Will be deleted:</strong> promoters · stores · shifts · stock entries · surveys</div>
            <div><strong>Will be kept:</strong> admin account · SKU master list (survey form options)</div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reset-confirm" className="text-xs font-mono uppercase tracking-wider">
              Type <span className="font-bold">RESET</span> to confirm
            </Label>
            <Input
              id="reset-confirm"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="off"
              className="rounded-xl border-slate-300/70"
              data-testid="data-reset-confirm-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-xl border-slate-300/70"
              data-testid="data-reset-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || confirm !== "RESET"}
              className="rounded-xl bg-rose-700 text-white hover:bg-rose-800 disabled:bg-neutral-300"
              data-testid="data-reset-submit"
            >
              {submitting ? "Deleting…" : "Delete everything"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default DataResetDialog;
